import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogV2Component } from 'src/app/global/components/dialogs/confirm-dialog-v2/confirm-dialog-v2.component';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { authOrgSelector } from 'src/app/auth/store/auth.selectors';
import { RequestStatus } from 'src/app/request-v2/models/request-status.model';
import { HttpErrorResponse } from '@angular/common/http';
import { StatusMigrationDialogComponent } from '../status-migration-dialog/status-migration-dialog.component';
import { RequestStatusService } from '../../../../services/request-status.service';
import { RequestsV2ImplService } from '../../../../services/requests-v2.impl.service';
import { MessageService } from '../../../../../global/services/message/message.abstract.service';

@Component({
    selector: 'app-status-list',
    templateUrl: './status-list.component.html',
    styleUrls: ['./status-list.component.scss'],
})
export class StatusListComponent implements OnInit, OnDestroy {
    @Output() edit = new EventEmitter<string>();

    statuses: RequestStatus[] = [];

    defaultStatuses: RequestStatus[] = [];

    loading = false;

    hasError = false;

    orgId = '';

    private destroy$ = new Subject<void>();

    constructor(
        private dialog: MatDialog,
        private translate: TranslateService,
        private messageService: MessageService,
        private requestsService: RequestsV2ImplService,
        private requestStatusService: RequestStatusService,
        private store: Store<State>,
    ) {}

    ngOnInit(): void {
        this.store
            .select(authOrgSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((orgId) => {
                this.orgId = orgId;
                this.loadStatuses();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadStatuses(): void {
        this.loading = true;
        this.hasError = false;

        forkJoin({
            orgStatuses: this.requestStatusService.getAllCreatedByOrg(),
            defaultStatuses: this.requestStatusService.getDefaultStatusList(),
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.statuses = result.orgStatuses;
                    this.defaultStatuses = result.defaultStatuses;
                    this.loading = false;
                },
                error: (_err) => {
                    this.loading = false;
                    this.hasError = true;
                    this.showErrorMessage('request_v2.status_list.load_error');
                },
            });
    }

    getStatusType(status: RequestStatus): string {
        const matchedDefaultStatus = this.defaultStatuses.find((defaultStatus) => defaultStatus._id === status.type);

        return matchedDefaultStatus ? (matchedDefaultStatus.name as string) : '';
    }

    onEdit(statusId: string): void {
        this.edit.emit(statusId);
    }

    onDelete(status: RequestStatus): void {
        if (!status.isDeletable) {
            return;
        }

        const confirmDialogRef = this.dialog.open(ConfirmDialogV2Component, {
            autoFocus: false,
            panelClass: 'bciti-confirmation-dialog-v2',
            data: {
                title: this.translate.instant('request_v2.status_list.delete.title'),
                body: this.translate.instant('request_v2.status_list.delete.body', {
                    name: status.name,
                }),
                buttons: {
                    confirm: {
                        text: this.translate.instant('common.delete'),
                        class: 'bciti-button bciti-button--red',
                    },
                    cancel: {
                        text: this.translate.instant('common.cancel'),
                        class: 'bciti-button bciti-button--blue-inversed',
                    },
                },
            },
        });

        confirmDialogRef.componentInstance.onSubmit.subscribe((confirm) => {
            if (confirm) {
                confirmDialogRef.componentInstance.loading = true;

                this.requestStatusService
                    .delete(status._id)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => {
                            this.statuses = this.statuses.filter((s) => s._id !== status._id);
                            confirmDialogRef.close();
                            this.showSuccessMessage('request_v2.status_list.delete_success');
                        },
                        error: (_err: HttpErrorResponse) => {
                            confirmDialogRef.componentInstance.loading = false;

                            if (_err?.error?.code === 'REQUEST_STATUS_IS_USED') {
                                this.openStatusMigrationDialog(status, confirmDialogRef);
                            } else {
                                this.showErrorMessage('request_v2.status_list.delete_error');
                            }
                        },
                    });
            } else {
                confirmDialogRef.close();
            }
        });
    }

    openStatusMigrationDialog(statusToDelete: RequestStatus, confirmDialogRef?: any): void {
        const dialogRef = this.dialog.open(StatusMigrationDialogComponent, {
            width: '490px',
            autoFocus: false,
            panelClass: 'status-migration-dialog-pane',
            data: {
                statusId: statusToDelete._id,
                statusName: statusToDelete.name,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result && result.migrated) {
                // Migration was successful
                if (result.deleted) {
                    // Both migration and deletion were successful
                    this.statuses = this.statuses.filter((s) => s._id !== statusToDelete._id);
                    if (confirmDialogRef) {
                        confirmDialogRef.close();
                    }
                } else if (confirmDialogRef) {
                    // Migration successful but deletion failed
                    confirmDialogRef.componentInstance.loading = false;
                }
                this.loadStatuses();
            } else if (confirmDialogRef) {
                // Migration was cancelled or failed
                confirmDialogRef.componentInstance.loading = false;
            }
        });
    }

    private showSuccessMessage(key: string): void {
        this.messageService.success(this.translate.instant(key));
    }

    private showErrorMessage(key: string): void {
        this.messageService.error(this.translate.instant(key));
    }
}
